import { useCallback, useEffect, useState } from "react";
import {
  catalogueProductLabel,
  INJECTION_GUIDE_SOURCE_KIND_LABEL,
  MEDICATION_SOURCE_FINDING_LABEL,
  type MedicationSourceCheckRun,
  type MedicationSourceReviewTask,
} from "@care-by-ella/contracts";
import { UnauthorizedError } from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import {
  fetchSourceReviewTasks,
  resolveSourceReviewTask,
  runSourceCheckNow,
} from "@/lib/medication-catalogue-api";
import { InjectionGuideRegister } from "./InjectionGuideRegister";

// TES-1293 (TES-1267 §8) — the internal review tasks the weekly check of the
// official instruction links opens. Admin and doctor see them; either
// resolves one by naming the page they verified against. Resolving changes
// nothing the patient sees: if the source is wrong, the link is switched off
// or changed as a new catalogue version below. The Swedish copy is proposed.

function fmtDateTime(iso: string | null): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("sv-SE", {
    timeZone: "Europe/Stockholm",
    dateStyle: "short",
    timeStyle: "short",
  });
}

function failText(e: unknown): string {
  return errorText(e instanceof Error ? e.message : String(e));
}

/** One line on what the latest check of a URL saw. */
export function checkSummary(
  c: MedicationSourceReviewTask["latestChecks"][number],
): string {
  const parts = [
    c.kind === "film"
      ? "Film"
      : c.kind === "instruction"
        ? "Instruktion"
        : INJECTION_GUIDE_SOURCE_KIND_LABEL[c.kind],
  ];
  parts.push(
    c.httpStatus !== null ? `HTTP ${c.httpStatus}` : (c.error ?? "inget svar"),
  );
  if (c.redirectChain.length > 1) {
    parts.push(`vidare till ${c.redirectChain[c.redirectChain.length - 1]}`);
  }
  if (c.pageUpdatedOn) parts.push(`sidan uppdaterad ${c.pageUpdatedOn}`);
  if (c.kind !== "instruction" && c.kind !== "film") parts.push(c.url);
  parts.push(`kontrollerad ${fmtDateTime(c.checkedAt)}`);
  return parts.join(" · ");
}

export function SourceReviewTasks({
  isAdmin,
  onResolved,
  onUnauthorized,
}: {
  isAdmin: boolean;
  onResolved: () => Promise<void>;
  onUnauthorized: () => void;
}) {
  const [tasks, setTasks] = useState<MedicationSourceReviewTask[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState<MedicationSourceCheckRun | null>(null);
  // Bumped after every load, so the guide register re-reads what the check
  // or a resolve just wrote.
  const [registerToken, setRegisterToken] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      setTasks((await fetchSourceReviewTasks()).tasks);
      setRegisterToken((n) => n + 1);
    } catch (e) {
      if (e instanceof UnauthorizedError) return onUnauthorized();
      setError(failText(e));
    }
  }, [onUnauthorized]);

  useEffect(() => {
    void load();
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    setError(null);
    try {
      setLastRun(await runSourceCheckNow());
      await load();
    } catch (e) {
      if (e instanceof UnauthorizedError) return onUnauthorized();
      setError(failText(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <section
      className="border border-surfaceMuted rounded-md bg-surface p-md flex flex-col gap-sm"
      data-testid="source-review-tasks"
    >
      <div className="flex flex-wrap items-baseline gap-sm">
        <h2 className="font-display text-xl">
          Granskning av officiella källor
        </h2>
        {isAdmin ? (
          <button
            type="button"
            disabled={running}
            onClick={() => void runNow()}
            className="ml-auto rounded-md border border-surfaceMuted px-md py-xs text-sm disabled:opacity-50"
          >
            {running ? "Kontrollerar…" : "Kontrollera källorna nu"}
          </button>
        ) : null}
      </div>
      <p className="text-sm text-textSecondary max-w-2xl">
        Varje måndag kontrolleras de aktiva instruktionslänkarna: att sidan
        svarar, att preparat och pennvariant står kvar och om innehållet eller
        uppdateringsdatumet har ändrats. Samma kontroll läser injektionsguidens
        källor i registret nedan. En ändring blir en uppgift här. Inget ändras
        automatiskt för patienten.
      </p>
      {lastRun ? (
        <p className="text-xs text-textSecondary">
          {lastRun.ran
            ? `${lastRun.checked} källor kontrollerade, ${lastRun.tasksOpened} nya uppgifter` +
              (lastRun.guideSourcesFailed > 0
                ? `, ${lastRun.guideSourcesFailed} guidekällor gick inte att läsa.`
                : ".")
            : "En kontroll pågår redan."}
        </p>
      ) : null}
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {tasks === null && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
      {tasks?.length === 0 ? (
        <p className="text-sm text-textSecondary">Inga öppna uppgifter.</p>
      ) : null}
      {tasks?.map((t) => (
        <TaskRow
          key={t.id}
          task={t}
          onResolved={async () => {
            await load();
            await onResolved();
          }}
          onUnauthorized={onUnauthorized}
        />
      ))}
      <InjectionGuideRegister
        reloadToken={registerToken}
        onUnauthorized={onUnauthorized}
      />
    </section>
  );
}

function TaskRow({
  task: t,
  onResolved,
  onUnauthorized,
}: {
  task: MedicationSourceReviewTask;
  onResolved: () => Promise<void>;
  onUnauthorized: () => void;
}) {
  const [source, setSource] = useState(
    t.instructionUrl ?? t.latestChecks[0]?.url ?? "",
  );
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSave = source.trim().startsWith("https://") && !busy;

  return (
    <div
      className="border-t border-surfaceMuted pt-sm flex flex-col gap-xs text-sm"
      data-testid={`source-review-${t.productKey ?? `guide-${t.guideVariantKey}`}`}
    >
      <div className="flex flex-wrap items-baseline gap-sm">
        <strong>
          {t.subject === "guide_variant"
            ? `Injektionsguide – ${t.guideVariantLabel ?? ""}`
            : catalogueProductLabel({
                name: t.productName ?? "",
                penVariant: t.penVariant ?? "",
              })}
        </strong>
        <span className="text-xs text-textSecondary">
          {t.subject === "catalogue_version"
            ? `Version ${t.version}${t.versionSuperseded ? " (ersatt av en nyare)" : ""} · `
            : ""}
          öppnad {fmtDateTime(t.openedAt)} · senast sedd{" "}
          {fmtDateTime(t.lastSeenAt)}
        </span>
      </div>
      {t.subject === "guide_variant" ? (
        <p className="text-xs text-textSecondary">
          Jämför källan med guidens text. Ändras guiden görs det som en ny
          version av artikeln, med medicinsk granskning; den här uppgiften
          ändrar ingenting i guiden.
        </p>
      ) : null}
      <ul className="list-disc pl-md">
        {t.findings.map((f) => (
          <li key={f}>{MEDICATION_SOURCE_FINDING_LABEL[f]}</li>
        ))}
      </ul>
      {t.latestChecks.map((c) => (
        <p
          key={`${c.kind} ${c.url}`}
          className="text-xs text-textSecondary break-all"
        >
          {checkSummary(c)}
        </p>
      ))}
      <form
        className="flex flex-col gap-xs"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSave) return;
          setBusy(true);
          setError(null);
          resolveSourceReviewTask(t.id, {
            source: source.trim(),
            note: note.trim() || null,
          })
            .then(onResolved)
            .catch((err: unknown) => {
              if (err instanceof UnauthorizedError) return onUnauthorized();
              setError(failText(err));
            })
            .finally(() => setBusy(false));
        }}
      >
        <label className="flex flex-col gap-xs">
          Källa som granskats (https://…)
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="px-sm py-xs rounded-md border border-surfaceMuted bg-surface text-sm text-textPrimary"
          />
        </label>
        <label className="flex flex-col gap-xs">
          Kommentar (valfri, intern)
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="px-sm py-xs rounded-md border border-surfaceMuted bg-surface text-sm text-textPrimary"
          />
        </label>
        {error ? <p className="text-danger">{error}</p> : null}
        <div>
          <button
            type="submit"
            disabled={!canSave}
            className="rounded-md bg-brand px-md py-xs text-sm text-white disabled:opacity-50"
          >
            Markera som granskad
          </button>
        </div>
      </form>
    </div>
  );
}
